<script>
  import { createEventDispatcher, onMount } from "svelte";
  import { Icon, ChevronDown, XMark } from "svelte-hero-icons";
  import { cx } from "$lib/utils/cx";
  import { persisted } from "$lib/utils/persisted-stores";
  import { liveStore } from "../Automator/store";

  export let value;
  export let label;
  export let selectedApp;
  export let isReadonly = false;
  export let isOpen = false;
  export let shouldPersist = null;
  export let persistedName = null;

  const dispatch = createEventDispatcher();

  let displayValue;

  let persistedStore = null;
  $: if (shouldPersist && $shouldPersist && persistedName) {
    persistedStore = persisted(persistedName, null);
    if ($persistedStore && selectedApp && typeof $persistedStore[selectedApp] === "object") {
      const persistedValue = $persistedStore[selectedApp];
      if (persistedValue?.id) {
        $liveStore.pushEvent("check_if_build_exists", {
          payload: {
            build_id: persistedValue.id,
          },
        });
      }
    }
  }

  $: {
    if (value) {
      displayValue = `${value.original_filename || "Unknown"} (${value.sha256?.slice(0, 8) || "Unknown"})`;
    } else {
      displayValue = "Production";
    }
  }

  function handleClick() {
    if (!isReadonly) {
      isOpen = true;

      $liveStore.pushEvent("open_build_selector", {
        payload: {
          app_id: selectedApp,
        },
      });
    }
  }

  function clearValue() {
    if (!isReadonly) {
      value = null;
      if (persistedStore && selectedApp) {
        $persistedStore[selectedApp] = null;
      }
      dispatch("change", null);
    }
  }

  function handleModalResult(payload) {
    handleModalClose(payload);

    value = payload.build;
    if (persistedStore) {
      persistedStore.update((persistedValue) => {
        // persistedValue is a key-value object, and the key is the app id
        // value is the build object
        if (persistedValue == null || typeof persistedValue !== "object") {
          persistedValue = {};
        }

        persistedValue[selectedApp] = payload.build;
        return persistedValue;
      });
    }

    dispatch("change", payload.build);
  }

  function handleModalClose(_payload) {
    isOpen = false;
  }

  function handleBuildExistsResult(payload) {
    if (payload?.exists) {
      value = payload.build;
    } else {
      if (persistedStore && selectedApp) {
        $persistedStore[selectedApp] = null;
      }
    }
  }

  onMount(() => {
    const selectedRef = $liveStore.handleEvent("build_selected", handleModalResult);
    const closedRef = $liveStore.handleEvent("build_selector_closed", handleModalClose);
    const fromPersistedRef = $liveStore.handleEvent("build_exists", handleBuildExistsResult);

    return () => {
      $liveStore.removeHandleEvent(selectedRef);
      $liveStore.removeHandleEvent(closedRef);
      $liveStore.removeHandleEvent(fromPersistedRef);
    };
  });
</script>

<div class="flex flex-col basis-full {isReadonly ? 'opacity-60' : ''}">
  <div aria-label="Select Build" class="w-full flex items-center {$$props.class || ''}">
    <div class="relative w-full">
      <button
        type="button"
        class="flex gap-2 items-center justify-between w-full h-8 text-left rounded p-2 pr-8 text-xs bg-surface-muted border-0 focus:outline-none truncate"
        class:cursor-not-allowed={isReadonly}
        class:focus:ring-2={!isReadonly}
        class:focus:ring-blue-500={!isReadonly}
        on:click={handleClick}
        disabled={isReadonly}
      >
        <span
          class="text-xs text-content-subtle whitespace-nowrap pointer-events-none transition-all truncate"
          class:cursor-not-allowed={isReadonly}
        >
          {label}
        </span>

        {displayValue}
      </button>

      <Icon
        src={ChevronDown}
        class={cx(
          "absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-content-subtle pointer-events-none",
          isReadonly && "opacity-50",
        )}
      />
    </div>
    <button
      type="button"
      class="ml-2 text-content-subtle hover:text-content focus:outline-none"
      on:click={clearValue}
    >
      <Icon src={XMark} class="w-4 h-4" />
    </button>
  </div>
</div>
